/**
 * Client-side image utilities.
 * - Compress large photos before upload (mobile cameras produce 3-8MB files).
 * - Detect in-app browsers (Facebook, Instagram, WhatsApp) where camera access is unreliable.
 */

/**
 * Compress an image File/Blob down to a target max side + JPEG quality.
 * Returns a new File (kept as JPEG). Preserves EXIF orientation via <img>.
 */
export async function compressImage(
  file,
  { maxSide = 1600, quality = 0.82, mimeType = "image/jpeg" } = {},
) {
  if (!file) return file;
  if (!/^image\//i.test(file.type)) return file;
  // Skip work for already-small files
  if (file.size < 800 * 1024 && file.type === mimeType) return file;

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });

  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), mimeType, quality),
  );
  if (!blob) return file;
  const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], newName, { type: mimeType, lastModified: Date.now() });
}

/**
 * Detect in-app WebViews where camera access is often blocked (Facebook,
 * Instagram, WhatsApp, Telegram, LinkedIn, TikTok, WeChat).
 */
export function isInAppBrowser() {
  const ua = (navigator.userAgent || "").toLowerCase();
  return /fbav|fban|instagram|line|whatsapp|telegram|linkedinapp|tiktok|micromessenger|snapchat|pinterest|twitter|threads/.test(ua);
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
